import { Alert, Anchor, Button, Checkbox, PasswordInput, Stack, Text, TextInput, Title } from '@mantine/core';
import { useForm } from '@mantine/form';
import { IconAlertCircle } from '@tabler/icons-react';
import { Trans, useTranslation } from 'react-i18next';
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router';
import { useSystemInfo } from '@/api/system';
import { ApiError } from '@/api/client';
import { useRegister, useSession } from '@/api/session';
import { useInvitation } from '@/api/workspace';
import { legalLinks } from '@/lib/legal';
import { AuthLayout } from './AuthLayout';

/** Registrierung (SEC-01), optional mit Einladungstoken. */
export function RegisterPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get('invitation') ?? undefined;
  const invitation = useInvitation(token);
  const system = useSystemInfo();
  const session = useSession();
  const register = useRegister();
  const legal = legalLinks(i18n.language);
  const form = useForm({
    initialValues: { name: '', email: '', password: '', acceptTerms: false },
    validate: {
      acceptTerms: (v) => (v ? null : t('register.termsRequired')),
      name: (v) => (v.trim() ? null : t('register.nameRequired')),
      email: (v) => (/^\S+@\S+$/.test(v) ? null : t('login.invalidEmail')),
      password: (v) => (v.length >= 8 ? null : t('register.passwordTooShort')),
    },
  });

  if (session.data?.authenticated && !token) {
    return <Navigate to="/tracker" replace />;
  }

  const error = register.error instanceof ApiError ? register.error : null;

  return (
    <AuthLayout>
      <form
        onSubmit={form.onSubmit((values) =>
          register.mutate(
            {
              ...values,
              email: values.email || invitation.data?.email || '',
              invitationToken: token,
              timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
              locale: i18n.language.startsWith('en') ? 'en' : 'de',
            },
            { onSuccess: () => navigate('/tracker', { replace: true }) },
          ),
        )}
      >
        <Stack>
          <Title order={3} ta="center">
            {t('register.title')}
          </Title>
          {invitation.data && (
            <Alert color="orange">{t('register.invitedTo', { workspace: invitation.data.workspaceName })}</Alert>
          )}
          {system.data?.registrationOpen === false && !token && <Alert color="gray">{t('register.closed')}</Alert>}
          {error && (
            <Alert color="red" icon={<IconAlertCircle size={18} />}>
              {error.code === 'email-taken'
                ? t('register.emailTaken')
                : error.code === 'registration-closed'
                  ? t('register.closed')
                  : error.code === 'terms-required'
                    ? t('register.termsRequired')
                    : error.message}
            </Alert>
          )}
          <TextInput label={t('register.name')} autoComplete="name" {...form.getInputProps('name')} />
          <TextInput
            label={t('login.email')}
            type="email"
            autoComplete="email"
            {...form.getInputProps('email')}
            placeholder={invitation.data?.email}
          />
          <PasswordInput label={t('login.password')} autoComplete="new-password" description={t('register.passwordHint')} {...form.getInputProps('password')} />
          <Checkbox
            {...form.getInputProps('acceptTerms', { type: 'checkbox' })}
            label={
              <Trans
                i18nKey="register.acceptTerms"
                components={{
                  terms: <Anchor href={legal.terms} target="_blank" rel="noopener" inherit />,
                  privacy: <Anchor href={legal.privacy} target="_blank" rel="noopener" inherit />,
                }}
              />
            }
          />
          <Button type="submit" fullWidth loading={register.isPending}>
            {t('register.submit')}
          </Button>
          <Text size="sm" ta="center">
            {t('register.haveAccount')}{' '}
            <Anchor component={Link} to="/login">
              {t('login.submit')}
            </Anchor>
          </Text>
        </Stack>
      </form>
    </AuthLayout>
  );
}
